/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { bindQuerySelector, el, hide, jsx, show } from '@amazon/vinyl-tsx'
import { getAuth, notSignedInOrPending } from '@/auth/authRef'
import { logDebug } from '@amazon/vinyl-util'
import QRCode from 'qrcode'
import { ctx } from '@/util/context'
import { AuthStatus } from '@/auth/auth'
import { getString } from '@/auth/i18n/bundle.generated'

const { target, errorHandler } = ctx('CodeLinkCallout')

export function CodeLinkCallout() {
    return (
        <div
            id="codeLinkCallout"
            className="codeLinkCallout"
            onConnect={() => {
                const auth = getAuth()
                const signIn = () => {
                    // CBL Auth needs to call signIn to initialize the code.
                    displayPublicCodePrompt(null)
                    auth.signIn()
                }

                return auth.status.onData((value) => {
                    if (value === AuthStatus.SIGNED_OUT) {
                        setTimeout(signIn)
                    }
                })
            }}
            visible={notSignedInOrPending()}
        >
            <div
                className="logo"
                role="img"
                aria-label={getString('dmusic_public_code_prompt_logo_accessibility_label')}
            />

            <h1>{getString('dmusic_public_code_prompt_title')}</h1>
            <div className="qrCode"></div>
            <span className="codeLink" />
            <div className="instructions">
                {getString('dmusic_public_code_prompt_instructions_prefix')}{' '}
                <a className="signInLink" target="_blank">
                    {getString('dmusic_public_code_prompt_code_url_link')}
                </a>{' '}
                {getString('dmusic_public_code_prompt_instructions_suffix')}
            </div>
        </div>
    )
}

/**
 * Updates the codeLinkCallout div with the public code.
 *
 * @param publicCode
 */
export function displayPublicCodePrompt(publicCode: string | null): () => void {
    logDebug(target, 'displayPublicCodePrompt', publicCode)
    const codeLinkCallout = el('#codeLinkCallout')
    const viewEl = bindQuerySelector(codeLinkCallout)
    viewEl('.codeLink').textContent = publicCode ?? '...'

    // A null code is the pre-fetch placeholder, not a dismissal: the prompt must
    // stay up showing '...' or a code request that never completes leaves the
    // device on a blank screen. Dismissal is the returned disposer.
    show(codeLinkCallout)

    const signInLink = viewEl('.signInLink') as HTMLAnchorElement
    if (publicCode) {
        const url = `https://www.amazon.com/code?cbl-code=${encodeURIComponent(publicCode)}&cbl-status=redirect_get_request`
        signInLink.href = url

        QRCode.toString(url, {
            type: 'svg',
            color: {
                dark: '#111111',
                light: '#ffffff',
            },
            margin: 2,
            width: 128,
        })
            .then((svg) => {
                viewEl('.qrCode').innerHTML = svg
            })
            .catch(errorHandler)
    } else {
        // Drop any previous code's link and QR — scanning a stale code next to
        // a '...' placeholder would send the user to a dead pairing.
        signInLink.removeAttribute('href')
        viewEl('.qrCode').innerHTML = ''
    }
    return () => hide(codeLinkCallout)
}
